import { SortByKind } from "@/modules/lenses/types";
import { SearchPageDateParams } from "@/modules/filters/search/SearchPageFilter";
import {
  SearchEngineRequestFilter,
  ValidSearchSortByValue,
} from "@/store/pages/SearchPageStore/types";
import { CalendarDate, getLocalTimeZone } from "@internationalized/date";

const DATE_ONLY_VALUE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Converts calendar date picker values into datetime bounds for the search API.
 */
function normalizeDateOnlyBoundary(value: string, addDays: number = 0): string {
  const match = DATE_ONLY_VALUE.exec(value);
  if (!match) return value;

  const [, year, month, day] = match;
  return new CalendarDate(Number(year), Number(month), Number(day))
    .add({ days: addDays })
    .toDate(getLocalTimeZone())
    .toISOString();
}

export function convertDateParamsToSearchEngineFilters(
  dateParams: SearchPageDateParams | undefined,
  sortBy: ValidSearchSortByValue | undefined
): SearchEngineRequestFilter[] {
  const output: SearchEngineRequestFilter[] = [];
  if (!dateParams) return output;

  const { olderThan, from, to } = dateParams;
  const shouldFilterCreatedAt = sortBy === SortByKind.LastCreated;
  const fromBoundary = from ? normalizeDateOnlyBoundary(from) : undefined;
  const toBoundary = to ? normalizeDateOnlyBoundary(to, 1) : undefined;

  if (olderThan) {
    const now = Date.now();
    const thresholds = {
      week: 7 * 24 * 60 * 60 * 1000,
      month: 30 * 24 * 60 * 60 * 1000,
      year: 365 * 24 * 60 * 60 * 1000,
    };
    const date = new Date(now - thresholds[olderThan]).toISOString();

    output.push(
      shouldFilterCreatedAt
        ? { kind: "CREATED_BEFORE", value: { created_before: date } }
        : { kind: "MODIFIED_BEFORE", value: { modified_before: date } }
    );
  } else {
    if (shouldFilterCreatedAt) {
      if (toBoundary)
        output.push({ kind: "CREATED_BEFORE", value: { created_before: toBoundary } });
      if (fromBoundary)
        output.push({ kind: "CREATED_AFTER", value: { created_after: fromBoundary } });
    } else {
      if (toBoundary)
        output.push({ kind: "MODIFIED_BEFORE", value: { modified_before: toBoundary } });
      if (fromBoundary)
        output.push({ kind: "MODIFIED_AFTER", value: { modified_after: fromBoundary } });
    }
  }

  return output;
}
